/**
 * ヘッダー管理モジュール
 * ヘッダーのスクロール連動表示とメニューアニメーションを制御
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// GSAPプラグインの登録
gsap.registerPlugin(ScrollTrigger);
import { HEADER_CONFIG, GSAP_CONFIG } from './config.js';

/**
 * ヘッダーのScrollTrigger設定を行う関数
 */
export function setupHeaderScrollTriggers() {
  if (!document.querySelector('body').classList.contains('home')) return;
  // MV セクションを基準にヘッダー表示・デザイン変更を切り替える
  ScrollTrigger.create({
    trigger: HEADER_CONFIG.scrollTriggers.mvTrigger.trigger,
    start: HEADER_CONFIG.scrollTriggers.mvTrigger.start,
    onEnter: function () {
      const header = document.querySelector('#global_header');
      header.classList.add('is-visible');
      header.classList.add('is-scrolled');
    },
    onLeaveBack: function () {
      const header = document.querySelector('#global_header');
      header.classList.remove('is-scrolled');
      header.classList.remove('is-visible');
    },
  });

  // ページ最上部にいる場合は常にヘッダーを表示
  ScrollTrigger.create({
    trigger: HEADER_CONFIG.scrollTriggers.topTrigger.trigger,
    end: HEADER_CONFIG.scrollTriggers.topTrigger.end,
    toggleClass: { targets: '#global_header', className: 'is-visible' }
  });
}

/**
 * モバイルメニューの開閉制御をセットアップ
 * ハンバーガーメニューの開閉アニメーションとアクセシビリティ対応
 */
export function setupMobileMenu() {
  const menuButton = document.getElementById('jsHeaderMenuButton');
  const mobileMenu = document.getElementById('jsMobileMenu');
  const menuButtonLines = document.querySelectorAll('.button_line');
  const headerMenusContainer = document.getElementById('header_menus');
  const body = document.querySelector('body');
  const headerMenuBg = document.getElementById('jsHeaderMenuBg');

  // 要素が存在しない場合は処理を終了
  if (!menuButton || !mobileMenu || !headerMenuBg || !headerMenusContainer) {
    return;
  }

  // 初期状態の設定（パフォーマンス最適化）
  gsap.set([headerMenuBg, headerMenusContainer], {
    opacity: 0,
    visibility: 'hidden',
  });

  // ボタンラインのtransform原点を設定
  gsap.set(menuButtonLines, {
    transformOrigin: 'center center',
  });

  // translateYの計算: gap(0.4rem) + ライン高さの半分(0.1rem) = 0.5rem
  // 上ラインは中央から0.5rem上、下ラインは中央から0.5rem下の位置から中央に移動
  const lineOffset = '0.5rem';

  // アニメーションタイムライン（高速化：0.15秒に統一、並列実行で高速化）
  const tl = gsap
    .timeline({ 
      paused: true, 
      reversed: true, 
      ease: 'power2.out'
    })
    .to(headerMenuBg, { 
      opacity: 1, 
      visibility: 'visible', 
      duration: 0.15 
    }, 0)
    .to(menuButtonLines[1], { 
      opacity: 0, 
      duration: 0.1 
    }, 0)
    .to(menuButtonLines[0], { 
      rotation: 45, 
      y: lineOffset,
      duration: 0.15 
    }, 0)
    .to(menuButtonLines[2], { 
      rotation: -45, 
      y: `-${lineOffset}`,
      duration: 0.15 
    }, 0)
    // コンテンツは少し遅れて表示（視覚的な階層感を保ちつつ高速化）
    .to(headerMenusContainer, { 
      opacity: 1, 
      visibility: 'visible', 
      duration: 0.15 
    }, 0.05);

  /**
   * メニューの開閉を切り替える関数
   * @param {Event} event - イベントオブジェクト
   */
  function toggleMenu(event) {
    if (event) {
      event.preventDefault();
    }

    const isReversed = tl.reversed();
    
    if (isReversed) {
      // メニューを開く
      tl.play();
      body.style.overflow = 'hidden';
      menuButton.setAttribute('aria-expanded', 'true');
      mobileMenu.setAttribute('aria-hidden', 'false');
    } else {
      // メニューを閉じる
      tl.reverse();
      body.style.overflow = '';
      menuButton.setAttribute('aria-expanded', 'false');
      mobileMenu.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * メニューを閉じる関数
   */
  function closeMenu() {
    if (!tl.reversed()) {
      tl.reverse();
      body.style.overflow = '';
      menuButton.setAttribute('aria-expanded', 'false');
      mobileMenu.setAttribute('aria-hidden', 'true');
    }
  }

  // ハンバーガーボタンのクリックイベント
  menuButton.addEventListener('click', toggleMenu);

  // メニュー内のリンクをクリックしたらメニューを閉じる
  const menuLinks = mobileMenu.querySelectorAll('.mobile_menu_link, .mobile_menu_action_btn');
  menuLinks.forEach((link) => {
    link.addEventListener('click', () => {
      // モーダルを開くリンクの場合は閉じない
      if (link.classList.contains('js-open-modal')) {
        return;
      }
      closeMenu();
    });
  });

  // ESCキーでメニューを閉じる
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !tl.reversed()) {
      closeMenu();
      menuButton.focus();
    }
  });

  // メニュー外をクリックしたら閉じる
  mobileMenu.addEventListener('click', (event) => {
    if (event.target === mobileMenu || event.target === headerMenuBg) {
      closeMenu();
    }
  });
}

/**
 * グローバルメニューアニメーションのセットアップ（後方互換性のため残す）
 * @deprecated setupMobileMenuを使用してください
 */
export function gsapGrobalMenuAnimation() {
  setupMobileMenu();
} 
